/**
 * API Endpoint Constants
 * Centralized endpoint definitions for the NanoTrader Copilot API
 */

export const ENDPOINTS = {
  // Chat endpoints
  CHAT: {
    CREATE: 'copilot/chat',
    MESSAGES: (chatId) => `copilot/chat/${chatId}/messages`,
    MESSAGE: (chatId) => `copilot/chat/${chatId}/message`,
    DELETE: (chatId) => `copilot/chat/${chatId}`,
    CLEAR: (chatId) => `copilot/chat/${chatId}/clear`,
    STREAM_MESSAGE: (chatId) => `copilot/stream/chat/${chatId}/message`,
    LIST: 'copilot/chats',
  },

  // User endpoints
  USER: {
    LOGIN: 'copilot/users/login',
    MESSAGE_LIMIT: 'copilot/mymessage/limit',
    API_KEYS: 'user/api-keys',
    API_KEY: (provider) => `user/api-keys/${provider}`,
    API_KEYS_STATUS: 'user/api-keys/status',
  },

  // Admin endpoints
  ADMIN: {
    USERS: 'copilot/admin/users',
    USERS_USAGE_SUMMARY: 'copilot/admin/users/usage-summary',
    USER_CONFIG: (userId) => `copilot/admin/users/${userId}/config`,
    USER_STATUS: (userId) => `copilot/admin/users/${userId}/status`,
    USER_RESET_USAGE: (userId) => `copilot/admin/users/${userId}/reset-usage`,
    ERRORS: 'copilot/admin/errors',
    ERROR_BY_ID: (errorId) => `copilot/admin/errors/${errorId}`,
    KEY_VALUE: 'copilot/admin/keyValue',
    KEY_VALUE_BY_KEY: (key) => `copilot/admin/keyValue/${key}`,
    KEY_VALUES: 'copilot/admin/keyValues',
    SERVICES_STATUS: 'copilot/admin/services/status',
    SERVICE_TEST: 'copilot/admin/services/test-execution',
    TOOL_TEST: 'copilot/admin/tools/execute',
    // Subscription admin endpoints
    SUBSCRIPTION_TIERS: 'admin/subscriptions/tiers',
    SUBSCRIPTION_TIER: (tierId) => `admin/subscriptions/tiers/${tierId}`,
    SUBSCRIPTION_USERS: 'admin/subscriptions/users',
    SUBSCRIPTION_USER: (userId) => `admin/subscriptions/users/${userId}`,
    SUBSCRIPTION_USER_TIER: (userId) => `admin/subscriptions/users/${userId}/tier`,
    SUBSCRIPTION_USER_USAGE: (userId, days = 30) => `admin/subscriptions/users/${userId}/usage?days=${days}`,
    SUBSCRIPTION_FEATURE_FLAGS: 'admin/subscriptions/feature-flags',
    SUBSCRIPTION_FEATURE_FLAG: (flagName) => `admin/subscriptions/feature-flags/${flagName}`,
    // Hierarchical feature flags
    FEATURE_FLAGS_ALL: 'admin/subscriptions/feature-flags/all',
    FEATURE_FLAGS_SET: 'admin/subscriptions/feature-flags/set',
    FEATURE_FLAGS_DELETE: 'admin/subscriptions/feature-flags/delete',
    // Economic Calendar
    CALENDAR_STATUS: 'copilot/admin/calendar/status',
    CALENDAR_REFRESH: 'copilot/admin/calendar/refresh',
    CALENDAR_EVENTS: 'copilot/admin/calendar/events',
    CALENDAR_SIMULATE: 'copilot/admin/calendar/simulate',
    CALENDAR_PROVIDER_ENABLED: (provider) => `copilot/admin/calendar/provider/${provider}/enabled`,
    CALENDAR_INTEGRITY_CHECK: 'copilot/admin/calendar/integrity-check',
    // User deletion
    USER_DELETE: (userId) => `copilot/admin/users/${userId}`,
    DELETION_HISTORY: 'copilot/admin/users/deletion-history',
    DELETION_RETRY: (id) => `copilot/admin/users/deletion-history/${id}/retry`,
    // OAuth client management
    OAUTH_CLIENTS: 'admin/oauth/clients',
    OAUTH_CLIENT: (clientId) => `admin/oauth/clients/${clientId}`,
    OAUTH_CLIENT_REGENERATE: (clientId) => `admin/oauth/clients/${clientId}/regenerate-secret`,
    // Service API Keys management
    SERVICE_KEYS: 'copilot/admin/service-keys',
    SERVICE_KEY: (keyId) => `copilot/admin/service-keys/${keyId}`,
    SERVICE_KEY_SCOPES: (keyId) => `copilot/admin/service-keys/${keyId}/scopes`,
    SERVICE_KEY_DELETE_PERMANENT: (keyId) => `copilot/admin/service-keys/${keyId}/permanent`,
  },

  // Model endpoints
  MODELS: {
    LIST: 'copilot/models',
    BY_ID: (modelId) => `copilot/models/${encodeURIComponent(modelId)}`,
  },

  // Graph/Chart endpoints
  GRAPH: {
    DATA: (instrument, timeframe, count, source) =>
      `copilot/graph?instrument=${instrument}&granularity=${timeframe}&count=${count}&source=${source}`,
  },

  // Stock search endpoints
  STOCK: {
    SEARCH: (query, limit = 10) => `copilot/stock/search?query=${encodeURIComponent(query)}&limit=${limit}`,
  },

  // Journal endpoints
  JOURNAL: {
    ENTRIES: (limit = 20, offset = 0, methodAnalytics = 'all', accountId = null) => {
      let url = `trader-journal/entries?limit=${limit}&offset=${offset}&methodAnalytics=${encodeURIComponent(methodAnalytics)}`;
      if (accountId) url += `&accountId=${accountId}`;
      return url;
    },
    ENTRIES_BASE: 'trader-journal/entries',
    ENTRY: (entryId) => `trader-journal/entries/${entryId}`,
    STATISTICS: (methodAnalytics = 'all', accountId = null) => {
      let url = `trader-journal/statistics?methodAnalytics=${encodeURIComponent(methodAnalytics)}`;
      if (accountId) url += `&accountId=${accountId}`;
      return url;
    },
    ACCOUNTS: 'trader-journal/accounts',
    ACCOUNT: (accountId) => `trader-journal/accounts/${accountId}`,
    ACCOUNT_WEBHOOK: (accountId) => `trader-journal/accounts/${accountId}/webhook`,
    ACCOUNT_WEBHOOK_REGENERATE: (accountId) => `trader-journal/accounts/${accountId}/webhook/regenerate-secret`,
    // Instrument-specific webhooks
    WEBHOOKS: 'trader-journal/webhooks',
    WEBHOOKS_FOR_ACCOUNT: (accountId) => `trader-journal/accounts/${accountId}/webhooks`,
    WEBHOOK: (webhookId) => `trader-journal/webhooks/${webhookId}`,
    WEBHOOK_REGENERATE: (webhookId) => `trader-journal/webhooks/${webhookId}/regenerate-secret`,
  },

  // System endpoints
  SYSTEM: {
    VERSION: 'sys/version',
  },

  // Config endpoints (public)
  CONFIG: {
    FEATURES: 'config/features',
    FEATURES_RESOLVED: 'config/features/resolved',
    FEATURES_REFRESH: 'config/features/refresh',
    TIERS: 'config/tiers',
    MY_SUBSCRIPTION: 'config/my-subscription',
    MY_USAGE: 'config/my-usage',
  },

  // Alpha Access endpoints
  ALPHA: {
    // Public endpoints (no auth required)
    STATUS: 'alpha/status',
    VALIDATE: (code) => `alpha/validate/${encodeURIComponent(code)}`,
    WAITLIST: 'alpha/waitlist',
    WAITLIST_STATUS: 'alpha/waitlist/status',
    // Admin endpoints
    WAVES: 'admin/alpha/waves',
    WAVE_STATUS: (waveId) => `admin/alpha/waves/${waveId}/status`,
    CODES: 'admin/alpha/codes',
    CODES_BY_WAVE: (waveId) => `admin/alpha/codes?waveId=${waveId}`,
    CODES_GENERATE: 'admin/alpha/codes/generate',
    CODE_DEACTIVATE: (codeId) => `admin/alpha/codes/${codeId}`,
    ADMIN_WAITLIST: (page = 0, size = 50) => `admin/alpha/waitlist?page=${page}&size=${size}`,
    WAITLIST_INVITE: 'admin/alpha/waitlist/invite',
    ADMIN_WAITLIST_ENTRY: (id) => `admin/alpha/waitlist/${id}`,
    WAITLIST_PROMOTE: (entryId) => `admin/alpha/waitlist/${entryId}/promote`,
    STATS: 'admin/alpha/stats',
  },

  // ConceptGate endpoints
  CONCEPTGATE: {
    // Account
    ACCOUNT: 'conceptgate/account',

    // Trade
    TRADE: 'conceptgate/trade',

    // Library rules (admin-owned)
    LIBRARY_RULES: 'conceptgate/library/rules',
    LIBRARY_RULE: (ruleId) => `conceptgate/library/rules/${ruleId}`,
    LIBRARY_RULE_ENABLED: (ruleId) => `conceptgate/library/rules/${ruleId}/enabled`,

    // User rules (user selections from library)
    USER_RULES: 'conceptgate/user/rules',
    USER_RULE_ENABLED: (userRuleId) => `conceptgate/user/rules/${userRuleId}/enabled`,
    USER_RULE: (userRuleId) => `conceptgate/user/rules/${userRuleId}`,
    USER_RULE_CONFIG: (userRuleId) => `conceptgate/user/rules/${userRuleId}/config`,
    USER_RULE_TEST: (userRuleId) => `conceptgate/user/rules/${userRuleId}/test`,

    // Config
    LLM_INSTRUCTIONS: 'conceptgate/config/llm-instructions',

    // Validation
    VALIDATE: 'conceptgate/validate',
    VALIDATE_STREAM: 'conceptgate/stream/validate',
    VALIDATIONS: 'conceptgate/validations',
    VALIDATION: (validationId) => `conceptgate/validations/${validationId}`,

    // Usage tracking
    USAGE_SUMMARY: 'conceptgate/usage/summary',
    USAGE_BY_MODEL: 'conceptgate/usage/by-model',

    // Subscription status
    SUBSCRIPTION: 'conceptgate/subscription',

    // Pipelines
    PIPELINES: 'conceptgate/pipelines',
    PIPELINES_ACTIVE: 'conceptgate/pipelines/active',
    PIPELINE: (pipelineId) => `conceptgate/pipelines/${pipelineId}`,
    PIPELINE_ACTIVATE: (pipelineId) => `conceptgate/pipelines/${pipelineId}/activate`,
    PIPELINE_DUPLICATE: (pipelineId) => `conceptgate/pipelines/${pipelineId}/duplicate`,
    PIPELINE_RULES: (pipelineId) => `conceptgate/pipelines/${pipelineId}/rules`,
    PIPELINE_RULES_REORDER: (pipelineId) => `conceptgate/pipelines/${pipelineId}/rules/reorder`,

    // Pipeline Webhooks
    PIPELINE_WEBHOOK: (pipelineId) => `conceptgate/pipelines/${pipelineId}/webhook`,
    PIPELINE_WEBHOOK_REGENERATE: (pipelineId) => `conceptgate/pipelines/${pipelineId}/webhook/regenerate-secret`,
    WEBHOOK_USAGE: 'conceptgate/webhook/usage',

    // API Call History
    HISTORY: 'conceptgate/history',
    HISTORY_ENTRY: (historyId) => `conceptgate/history/${historyId}`,
    HISTORY_STATS: 'conceptgate/history/stats',
    HISTORY_DAILY_STATS: 'conceptgate/history/daily-stats',
    HISTORY_HOURLY_STATS: 'conceptgate/history/hourly-stats',
    HISTORY_PIPELINES: 'conceptgate/history/pipelines',
  },

  // Analytics Pipeline endpoints
  ANALYTICS: {
    // Library modules (admin-owned)
    LIBRARY_MODULES: 'analytics/library/modules',

    // Pipelines
    PIPELINES: 'analytics/pipelines',
    PIPELINES_ACTIVE: 'analytics/pipelines/active',
    PIPELINE: (pipelineId) => `analytics/pipelines/${pipelineId}`,
    PIPELINE_ACTIVATE: (pipelineId) => `analytics/pipelines/${pipelineId}/activate`,
    PIPELINE_DUPLICATE: (pipelineId) => `analytics/pipelines/${pipelineId}/duplicate`,
    PIPELINE_MODULES: (pipelineId) => `analytics/pipelines/${pipelineId}/modules`,
    PIPELINE_MODULE: (pipelineId, moduleId) => `analytics/pipelines/${pipelineId}/modules/${moduleId}`,
    PIPELINE_MODULES_REORDER: (pipelineId) => `analytics/pipelines/${pipelineId}/modules/reorder`,

    // Webhook
    PIPELINE_WEBHOOK: (pipelineId) => `analytics/pipelines/${pipelineId}/webhook`,
    PIPELINE_WEBHOOK_REGENERATE: (pipelineId) => `analytics/pipelines/${pipelineId}/webhook/regenerate-secret`,

    // Analysis execution
    ANALYZE: 'analytics/analyze',
    ANALYZE_STREAM: 'analytics/stream/analyze',

    // History
    HISTORY: 'analytics/history',
    EXECUTION: (executionId) => `analytics/executions/${executionId}`,
  },
};
